/* Created by Interactive 63 - https://interactive63.com */
/**
 * CSS-only shape morphing: `@keyframes` that animate the CSS `d` property between normalised
 * paths. It works in Chrome, Edge and Firefox because the shapes share one command structure,
 * but not in Safari, which is why the runtime is the recommended way to morph.
 */
import { easingToCss } from '../easing/to-css';
import { formatNumber } from '../easing/format-number';
import { formatPath } from '../morph/format';
import type { MorphBuild } from '../morph/morph-track';
import { trackTiming } from './css';
import type { SceneTiming } from './types';

/** The name of a morph track's @keyframes block. */
function keyframesName(sceneName: string, build: MorphBuild): string {
  return `st-${sceneName}-${build.elementId}-morph`;
}

/** A single keyframe step placed at a time within the scene, holding the given declarations. */
function keyframeBlock(time: number, duration: number, lines: readonly string[]): string {
  const percent = `${formatNumber((time / duration) * 100, 3)}%`;
  return `  ${percent} {\n${lines.map((l) => `    ${l};`).join('\n')}\n  }`;
}

/** The `animation` list entry for each morph track, keyed by element, for merging into its rule. */
export function morphAnimationEntries(
  builds: readonly MorphBuild[],
  timing: SceneTiming,
  sceneName: string,
): Map<string, string> {
  const entries = new Map<string, string>();
  for (const build of builds) {
    if (!build.result.ok) continue;
    entries.set(
      build.elementId,
      `${keyframesName(sceneName, build)} ${trackTiming(build.track, timing)}`,
    );
  }
  return entries;
}

/**
 * The CSS for every morph track that built successfully. Elements named in `merged` already run
 * their morph from another rule, so only the keyframes are written for them.
 */
export function morphCss(
  builds: readonly MorphBuild[],
  timing: SceneTiming,
  sceneName: string,
  sceneClass: string,
  merged: ReadonlySet<string> = new Set(),
): string {
  const sections: string[] = [];
  for (const build of builds) {
    if (!build.result.ok) continue;
    const { data, easings } = build.result;
    const name = keyframesName(sceneName, build);
    const last = data.frames.length - 1;
    const blocks = data.frames.map((frame, i) => {
      const lines = [`d: path("${formatPath(data.template, frame.values)}")`];
      const easing = easings[i];
      if (easing && i < last) {
        const css = easingToCss(easing);
        if (css.fallback) lines.push(`animation-timing-function: ${css.fallback}`);
        lines.push(`animation-timing-function: ${css.value}`);
      }
      return keyframeBlock(frame.time, timing.duration, lines);
    });
    // Without a keyframe at each end of the scene, CSS eases to and from the element's own `d`,
    // so the shape would drift back to the artwork's original path outside the keyframes.
    const first = data.frames[0];
    const final = data.frames[last];
    if (first && first.time > 0) {
      blocks.unshift(
        keyframeBlock(0, timing.duration, [
          `d: path("${formatPath(data.template, first.values)}")`,
        ]),
      );
    }
    if (final && final.time < timing.duration) {
      blocks.push(
        keyframeBlock(timing.duration, timing.duration, [
          `d: path("${formatPath(data.template, final.values)}")`,
        ]),
      );
    }
    if (!merged.has(build.elementId)) {
      sections.push(
        `.${sceneClass} .${data.className} {\n  animation: ${name} ${trackTiming(build.track, timing)};\n}`,
      );
    }
    sections.push(`@keyframes ${name} {\n${blocks.join('\n')}\n}`);
  }
  return sections.join('\n\n');
}

/** The reduced motion rules that hold a morph on its first or last shape. */
export function morphReducedMotionRules(
  builds: readonly MorphBuild[],
  sceneClass: string,
  pose: 'initial' | 'final',
): string[] {
  const rules: string[] = [];
  for (const build of builds) {
    if (!build.result.ok) continue;
    const d = pose === 'initial' ? build.initial : build.final;
    if (d === null) continue;
    rules.push(
      `.${sceneClass} .${build.result.data.className} {\n  animation: none !important;\n  d: path("${d}");\n}`,
    );
  }
  return rules;
}
